/**
 * attachedDocs — shared state contract for per-person document attachments
 * in the PersonDialog tree.
 *
 * Bill 2026-04-24: the CIP/ID Document Capture panel (top of dialog) and the
 * Document Requirements slot grid (bottom) must be connected. When the FSR
 * scans a Maryland DL at the top, the `GOVT_ID` slot at the bottom should
 * auto-fulfill with that image — officer should NOT have to upload the same
 * document twice.
 *
 * This module provides:
 *   - `AttachedDoc` — normalized shape covering both capture sources
 *     (CIP scan and manual upload/Add-existing)
 *   - `getPrimaryIdDocCode(entityType)` — maps the entity-type to the
 *     docCode that the scanned image should fulfill. Hardcoded today;
 *     will migrate to a catalog flag (`IsPrimaryIdentityDocument`) on
 *     the DocumentRequirements SQL row once that column ships.
 */

/** Source of the attached document. */
export type AttachedDocSource = 'cip-scan' | 'upload' | 'add-existing';

/** One document pinned to the person-in-dialog, keyed by docCode. */
export interface AttachedDoc {
  /** Target slot's docCode (matches DocumentRequirements.DocCode). */
  docCode: string;
  /** How the document entered the dialog — renders a source badge. */
  source: AttachedDocSource;
  /** Human filename for display ("Scanned ID.jpg", "drivers-license.pdf"). */
  filename: string;
  /** MIME type ("image/jpeg", "application/pdf"). */
  contentType: string;
  /** Size in bytes. 0 when the source is a scan and we haven't weighed the blob. */
  sizeBytes: number;
  /**
   * For image sources (scan + image upload), a data URL suitable for an
   * inline <img src>. Empty string when the source is a PDF or when the
   * thumbnail isn't available.
   */
  imageDataUrl: string;
  /** Persisted storage URL (SharePoint / blob). Empty until upload lands. */
  fileUrl: string;
  /** ISO timestamp — when the attachment landed. */
  capturedAt: string;
  /** SharePoint item id once the upload lands (v0.14.x cleanup). */
  spItemId?: string;
  /** Hex-encoded SHA-256 of the persisted binary. */
  sha256?: string;
  /** Azure DI raw document-type label returned by the classifier. */
  azureDiRawType?: string;
  /** Classifier confidence 0-1 for the persisted blob. */
  azureDiConfidence?: number;
}

/**
 * Entity-type → primary-ID docCode. Scanning a DL / passport / etc. in the
 * CIP panel auto-fulfills this code.
 *
 * Rule of thumb: this is the `Hard` + `SortOrder=1` row for the entity that
 * represents a government-issued photo ID of the Individual-in-dialog.
 *
 * Entity types whose primary ID is captured under a DIFFERENT person's
 * dialog (e.g., Trust → Trustees are separate Individual dialogs) return
 * `null` — the scan panel renders but the capture doesn't auto-fulfill a
 * slot in THIS dialog, because the trust entity itself has no personal ID.
 */
const PRIMARY_ID_DOC_CODE: Record<string, string> = {
  Individual: 'GOVT_ID',
  Minor: 'GOVT_ID_GUARDIAN',
  NonCitizen: 'PASSPORT',
  UTMA: 'CUSTODIAN_ID',
  RepPayee: 'REP_PAYEE_ID',
  Conservator: 'CONSERVATOR_ID',
  VAFiduciary: 'FIDUCIARY_ID',
};

export function getPrimaryIdDocCode(entityType: string | undefined | null): string | null {
  if (!entityType) return null;
  return PRIMARY_ID_DOC_CODE[entityType] ?? null;
}

/**
 * Approximate size of a base64-encoded blob. The byte length of the decoded
 * content is 3/4 of the base64 string length, minus padding. Close enough
 * for display — we don't rely on this for storage quotas.
 */
export function estimateBase64Size(base64: string): number {
  if (!base64) return 0;
  const padding = (base64.match(/=+$/) || [''])[0].length;
  return Math.floor((base64.length * 3) / 4) - padding;
}

// -----------------------------------------------------------------------------
// Persistence path (Contract C2 — BR owns the shape + the save path)
//
// Slot-4 T0 / BACKLOG-0569 data-loss fix. Captured docs MUST live on the
// REAL person draft (`person.extra.attachedDocs`), not in a sibling useState
// that re-seeds to `[]` on every prop change and only injects into a throwaway
// badge memo. These helpers are the single canonical read/merge/persist path;
// every PersonDialog body (Individual + the 7 entity dialogs) uses them so the
// docs survive `onDraftChange → onSave → peopleJson` and re-hydrate on reopen.
// -----------------------------------------------------------------------------

/** Minimal shape we need — avoids a circular import on RaoPerson. */
interface PersonWithExtra {
  extra?: Record<string, unknown>;
}

/**
 * Read the persisted attachedDocs off a person draft. Defensive: tolerates
 * a missing `extra`, a non-array value, or array entries that aren't doc-shaped
 * (returns only the well-formed rows). Pure — never mutates the input.
 */
export function readAttachedDocs(person: PersonWithExtra | undefined | null): ReadonlyArray<AttachedDoc> {
  const raw = person?.extra?.['attachedDocs'];
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (d): d is AttachedDoc =>
      !!d && typeof d === 'object' && typeof (d as AttachedDoc).docCode === 'string',
  );
}

/**
 * Storage-shape normalizer (Bill Q3 — default to REFERENCE).
 *
 * `peopleJson` is a single SQL column; inlining base64 image blobs would bloat
 * every cart row. So when a doc carries a storage reference (`fileUrl` or
 * `spItemId`), we DROP the heavy inline `imageDataUrl` on persist — the Edit-
 * mode hydration fetch (Slot-4 T5) re-fetches the binary from the reference.
 * When NO reference exists yet (a fresh CIP scan that hasn't landed in
 * SharePoint), we keep the inline data URL as the only copy so it isn't lost.
 *
 * ⚠ DEFAULT pending Bill's Q3 confirmation — flagged in the BR report.
 */
export function toPersistedAttachedDoc(doc: AttachedDoc): AttachedDoc {
  const hasReference = !!(doc.fileUrl || doc.spItemId);
  if (hasReference && doc.imageDataUrl) {
    return { ...doc, imageDataUrl: '' };
  }
  return doc;
}

/**
 * Merge one captured/uploaded doc into a person draft, keyed by `docCode`
 * (re-capturing a slot replaces the prior attachment). Returns a NEW person
 * with `extra.attachedDocs` updated through the canonical persisted shape.
 * Pure — safe to call inside a `setLocal(prev => mergeAttachedDoc(prev, doc))`.
 */
export function mergeAttachedDoc<T extends PersonWithExtra>(person: T, doc: AttachedDoc): T {
  const current = readAttachedDocs(person);
  const next = [...current.filter((d) => d.docCode !== doc.docCode), toPersistedAttachedDoc(doc)];
  return {
    ...person,
    extra: { ...(person.extra ?? {}), attachedDocs: next },
  };
}

/**
 * Merge many docs at once (e.g., Edit-mode hydration seeding several prior
 * captures, or a multi-slot drop). Later entries win on docCode collision.
 */
export function mergeAttachedDocs<T extends PersonWithExtra>(
  person: T,
  docs: ReadonlyArray<AttachedDoc>,
): T {
  let acc = person;
  for (const d of docs) acc = mergeAttachedDoc(acc, d);
  return acc;
}

/**
 * Replace the draft's attachedDocs with an authoritative running set, through
 * the canonical persisted shape. Used when a single surface OWNS the doc set
 * for a stage (PdwShell Stage 4: PdwStage4DocsGrid emits its complete running
 * set on every capture/delete) — unlike `mergeAttachedDoc`, this honors
 * removals (a docCode dropped from `docs` is dropped from the draft). Pure.
 */
export function setAttachedDocs<T extends PersonWithExtra>(
  person: T,
  docs: ReadonlyArray<AttachedDoc>,
): T {
  return {
    ...person,
    extra: { ...(person.extra ?? {}), attachedDocs: docs.map(toPersistedAttachedDoc) },
  };
}

/**
 * Seed prior docs WITHOUT clobbering ones already on the draft. Used by the
 * Edit-mode doc-library hydration (Slot-4 T5): a doc-library row only fills a
 * docCode the FSR hasn't already captured/edited this session. A fresh
 * in-session capture always wins over the stored reference.
 */
export function mergeAttachedDocsKeepExisting<T extends PersonWithExtra>(
  person: T,
  incoming: ReadonlyArray<AttachedDoc>,
): T {
  const existing = readAttachedDocs(person);
  const haveCodes = new Set(existing.map((d) => d.docCode));
  const toAdd = incoming.filter((d) => !haveCodes.has(d.docCode));
  if (toAdd.length === 0) return person;
  return mergeAttachedDocs(person, toAdd);
}
